import { useEffect, useState } from 'react';

/**
 * Detects when a large fixed-position panel occupies the bottom-right corner
 * of the viewport — used to auto-hide our floating Feedback FAB whenever the
 * Emergent preview chat bubble (or any other bottom-right widget) is open.
 *
 * Heuristic: any fixed-position element anchored within 60px of the viewport's
 * bottom-right corner whose width OR height exceeds `expandedThreshold` px is
 * treated as an "open panel". Ignores the passed-in refs (our own FAB).
 *
 * @param {number} expandedThreshold  px above which the widget is considered open (default 200)
 * @param {HTMLElement[]} ignoreEls   elements to skip (e.g. the FAB itself)
 * @returns {boolean}                 true when a bottom-right panel is expanded
 */
export function useBottomRightPanelOpen(expandedThreshold = 200, ignoreEls = []) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let rafId = null;

    const check = () => {
      rafId = null;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      let found = false;
      // Only walk elements that Emergent / third-party widgets typically use.
      const candidates = document.querySelectorAll('body > div, body > iframe, body > section, body > aside');
      for (const el of candidates) {
        if (ignoreEls.includes(el)) continue;
        const cs = getComputedStyle(el);
        if (cs.position !== 'fixed' || cs.display === 'none' || cs.visibility === 'hidden') continue;
        const r = el.getBoundingClientRect();
        const anchoredBR = r.right >= vw - 60 && r.bottom >= vh - 60;
        if (!anchoredBR) continue;
        if (r.width >= expandedThreshold || r.height >= expandedThreshold) {
          found = true;
          break;
        }
      }
      setOpen((prev) => (prev === found ? prev : found));
    };

    const schedule = () => {
      if (rafId != null) return;
      rafId = window.requestAnimationFrame(check);
    };

    const observer = new MutationObserver(schedule);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    });
    window.addEventListener('resize', schedule);
    schedule();

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', schedule);
      if (rafId != null) window.cancelAnimationFrame(rafId);
    };
    // ignoreEls is intentionally not in deps — passing a stable ref array from the caller
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedThreshold]);

  return open;
}
